import Link from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import { TaskItem } from "@tiptap/extension-task-item";
import { TaskList } from "@tiptap/extension-task-list";
import { Underline } from "@tiptap/extension-underline";
import {
  Content,
  EditorContent,
  EditorProvider,
  Extensions,
  isTextSelection,
  useEditor,
} from "@tiptap/react";
import { StarterKit } from "@tiptap/starter-kit";
import { useEffect, useRef } from "react";
import { twMerge } from "tailwind-merge";
import { Divider } from "../divider";
import { Bold, Italic1, Link1, Underline as UnderlineIcon } from "../icons";
import { BubbleMenu } from "./bubble-menu";
import {
  FormattingMenuButton,
  FormattingMenuContent,
  FormattingMenuHeadingSelect,
  FormattingMenuListSelect,
  FormattingMenuToggleButton,
} from "./formatting-menu";
import "./rich-text-editor.css";
import { useMediaQuery } from "../hooks/media-query";
import { LinkBubbleMenu } from "./link-bubble-menu";

const baseExtensions = [
  StarterKit.configure(),
  Underline,
  // Typography,
  // BulletList,
  // OrderedList,
  // ListItem,
  // Heading,
  TaskList,
  TaskItem.configure({
    nested: true,
  }),
];

const RichTextViewer = ({
  value,
  extensions = [],
  "data-cy": dataCy,
}: {
  value: Content | undefined;
  extensions?: Extensions;
  "data-cy"?: string;
}) => {
  const editor = useEditor({
    extensions: [...baseExtensions, Link, ...extensions],
    editorProps: {
      attributes: {
        class: "richTextStyling",
        "data-cy": dataCy ?? "rich-text-viewer",
      },
    },
    editable: false,
    content: value,
  });

  useEffect(() => {
    if (editor && value) {
      setTimeout(() => {
        editor.commands.setContent(value);
      });
    }
  }, [value, editor]);

  if (!editor) return null;

  return <EditorContent editor={editor} />;
};

const RichTextEditor = ({
  value,
  onChange,
  placeholder,
  extensions = [],
  "data-cy": dataCy,
  className,
}: {
  value: Content | undefined;
  onChange: (val: Content | undefined) => void;
  placeholder?: string;
  "data-cy"?: string;
  extensions?: Extensions;
  className?: string;
}) => {
  const isDesktop = useMediaQuery("md");
  const bubbleMenuToolbarRef = useRef<HTMLDivElement | null>(null);
  const mobileToolbarRef = useRef<HTMLDivElement | null>(null);
  return (
    <div
      className={twMerge(
        "border-grey-300 ring-grey-200/70 focus:border-grey-300 shadow-xs rounded-lg border bg-white outline-hidden transition-all focus-within:ring-3",
        className,
      )}
    >
      <EditorProvider
        extensions={[
          ...baseExtensions,
          Placeholder.configure({ placeholder }),
          Link.extend({ inclusive: false }).configure({
            openOnClick: false, // Prevents default behavior
          }),
          ...extensions,
        ]}
        editorProps={{
          attributes: {
            class: twMerge(
              "px-3.5 py-2.5 min-h-20",
              "richTextStyling richTextEditor",
            ),
            "data-cy": dataCy ?? "rich-text-editor",
          },
        }}
        content={value}
        onUpdate={(editor) => {
          onChange(editor.editor.getJSON());
        }}
      >
        <div className="border rounded-lg border-grey-200 md:hidden w-fit">
          <FormattingMenuContent ref={mobileToolbarRef}>
            <FormattingMenuHeadingSelect containerRef={mobileToolbarRef} />
            <Divider direction="vertical" className="h-6" />
            <div className="flex gap-px items-center">
              <FormattingMenuToggleButton value="bold">
                <Bold className="h-4 w-4" />
              </FormattingMenuToggleButton>
              <FormattingMenuToggleButton value="italic">
                <Italic1 className="h-4 w-4" />
              </FormattingMenuToggleButton>
              <FormattingMenuToggleButton value="underline">
                <UnderlineIcon className="h-4 w-4" />
              </FormattingMenuToggleButton>
            </div>
            <Divider direction="vertical" className="h-6" />
            <FormattingMenuListSelect containerRef={mobileToolbarRef} />
            <Divider direction="vertical" className="h-6" />
            <FormattingMenuButton value="link">
              <Link1 />
            </FormattingMenuButton>
          </FormattingMenuContent>
        </div>
        <LinkBubbleMenu />
        <BubbleMenu
          pluginKey="formatting-menu"
          shouldShow={({ editor, view, state, from, to }) => {
            if (!isDesktop) return false;
            // Reworked from the default, because we only want the selection
            // menu for text selections where a mark change will be visible.
            // https://github.com/ueberdosis/tiptap/blob/063ced27ca55f331960b01ee6aea5623eee0ba49/packages/extension-bubble-menu/src/bubble-menu-plugin.ts#L43
            if (!view.hasFocus()) return false;
            const { doc, selection } = state;
            const isText = isTextSelection(selection);
            if (!isText) return false;
            const isEmpty =
              selection.empty ||
              (isText && doc.textBetween(from, to).length === 0);
            if (isEmpty) return false;
            if (editor.isActive("link")) return false;
            if (editor.isActive("entityMention")) return false;
            return true;
          }}
        >
          <FormattingMenuContent ref={bubbleMenuToolbarRef}>
            <FormattingMenuHeadingSelect containerRef={bubbleMenuToolbarRef} />
            <Divider direction="vertical" className="h-6" />
            <div className="flex gap-px items-center">
              <FormattingMenuToggleButton value="bold">
                <Bold className="h-4 w-4" />
              </FormattingMenuToggleButton>
              <FormattingMenuToggleButton value="italic">
                <Italic1 className="h-4 w-4" />
              </FormattingMenuToggleButton>
              <FormattingMenuToggleButton value="underline">
                <UnderlineIcon className="h-4 w-4" />
              </FormattingMenuToggleButton>
            </div>
            <Divider direction="vertical" className="h-6" />
            <FormattingMenuListSelect containerRef={bubbleMenuToolbarRef} />
            <Divider direction="vertical" className="h-6" />
            <FormattingMenuButton value="link">
              <Link1 />
            </FormattingMenuButton>
          </FormattingMenuContent>
        </BubbleMenu>
      </EditorProvider>
    </div>
  );
};

export { RichTextEditor, RichTextViewer };
